import React, {
  FunctionComponent, useState, useCallback, ReactNode, useMemo,
} from 'react';
import FilterableListContext, { AsyncSuggestionListContextValue } from './AsyncSuggestionListContext';
import { SelectableItemProps } from '../selectable-item/SelectableItem';
import SearchInput, { SearchInputProps } from './input/SearchInput';
import SuggestionList, { SuggestionListProps } from './items/SuggestionList';

const MIN_CHARACTERS_TO_START_FILTERING = 2;

export type AsyncSuggestionListChildrenFunction = (props: AsyncSuggestionListContextValue) => ReactNode;

export interface AsyncFilterableListProps {
  fetcher: (searchTerm: string) => Promise<SelectableItemProps[]>,
  initialItems?: SelectableItemProps[],
  onItemSelect: (item?: SelectableItemProps) => void,
  minFilteringCharacters?: number,
  children: AsyncSuggestionListChildrenFunction | ReactNode,
}

interface AsyncFilterableListComposition {
  Input?: FunctionComponent<SearchInputProps>,
  Items?: FunctionComponent<SuggestionListProps>,
}

const AsyncFilterableList: FunctionComponent<AsyncFilterableListProps> & AsyncFilterableListComposition = ({
  fetcher,
  initialItems,
  children,
  minFilteringCharacters,
  onItemSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>();
  const [items, setItems] = useState(initialItems || []);
  const [error, setError] = useState();

  const minCharacters = minFilteringCharacters || MIN_CHARACTERS_TO_START_FILTERING;

  const onInputChange = useCallback(async (term: string) => {
    setSearchTerm(term);
    setError(undefined);

    // Clear results list
    if (term.length === 0) {
      setItems([]);
    }

    if (term.length >= minCharacters) {
      try {
        const results = await fetcher(term);
        setItems(results);
      } catch (err) {
        setError(err);
      }
    }
  }, [fetcher, minCharacters]);

  const onInternalItemSelect = useCallback((item) => {
    onItemSelect(item);
  }, [onItemSelect]);

  const context = useMemo(() => ({
    searchTerm,
    items,
    onItemSelect: onInternalItemSelect,
    onInputChange,
    error,
    minFilteringCharacters: minCharacters,
  }), [error, items, minCharacters, onInputChange, onInternalItemSelect, searchTerm]);

  return (
    <FilterableListContext.Provider
      value={context}
    >
      {(typeof children === 'function'
        ? (children as AsyncSuggestionListChildrenFunction)(context)
        : children)}
    </FilterableListContext.Provider>
  );
};

AsyncFilterableList.Input = SearchInput;

AsyncFilterableList.Items = SuggestionList;

export default AsyncFilterableList;
